/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */

/* eslint-disable no-empty-function */


import { RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { CoordinatePoint2, nearestSixteenth, Size, Vertex2 } from '@rta/rta-blueprint-library';

import { useHoverActivatedMouse } from './use-hover-activated-mouse';

type BBox = {
	origin: Vertex2;
	size: Size;
	padding: [number, number, number, number];
};

type eventMethod<R> = (event?: {
	position: {
		old: CoordinatePoint2<number>;
		new: CoordinatePoint2<number>;
	};
	factors: {
		old: CoordinatePoint2<number>;
		new: CoordinatePoint2<number>;
	};
	setBBox?: (bBox: BBox) => void;
}) => R;

export type UseDraggableSvgProps = {
	ref: RefObject<SVGElement>;
	cursor?: [string, string];
	// enable [x, y] for dragging
	enabled?: [boolean, boolean];
	// offset: CoordinatePoint2<number>;
	bBox?: BBox;
	factors?: CoordinatePoint2<number>;
	// used to override automatic ref positioning when provided
	position?: CoordinatePoint2<number>;
	setBBox?: (bBox: BBox) => void;
	// counter-rotates screen-space drag deltas into local coordinate space
	parentRotation?: number;
	// drag events
	onDragStart?: eventMethod<void>;
	onDragEnd?: eventMethod<boolean>;
	onDragging?: eventMethod<boolean>;
};

export type UseDraggableSvgPosition = {
	position: CoordinatePoint2<number>;
	factors: CoordinatePoint2<number>;
};

type UseDraggableSvg = [position: UseDraggableSvgPosition, setBBox: (bBox: BBox) => void];

export const useDraggableSvg = (props: UseDraggableSvgProps): UseDraggableSvg => {
	const { ref, cursor = ['inherit', 'move'], enabled = [true, true], factors = { x: 0.5, y: 0.5 }, position: positionOverride = null, parentRotation = 0 } = props;
	const { onDragStart = () => {}, onDragEnd = () => true, onDragging = () => true } = props;
	const [xMove, yMove] = enabled;

	// function to calculate factors
	const calcFactors = (p: CoordinatePoint2<number>, b: BBox): CoordinatePoint2<number> => {
		const f = {
			x: (p.x - (b.origin.x - (b.size.width / 2))) / b.size.width,
			y: (p.y - (b.origin.y - (b.size.height / 2))) / b.size.height
		};
		return f;
	};

	const calcPosition = (svgElement: SVGSVGElement | null): CoordinatePoint2<number> | null => {
		if (svgElement !== null) {
			const bbox = svgElement.getBBox();

			const elementCenter = svgElement
				? {
					x: nearestSixteenth(bbox.x + (bbox.width / 2)),
					y: nearestSixteenth(bbox.y + (bbox.height / 2))
				}
				: {
					x: 0,
					y: 0
				};
			// console.log('use-draggable-svg', ref?.current?.id, ref?.current?.getAttribute('x'), elementCenter.x, props.bBox?.origin.x);
			return elementCenter;
		}

		return null;
	};

	const [mouse] = useHoverActivatedMouse(ref, { cursor, enabled: xMove || yMove });
	// console.log('use-draggable-svg:mp', mouse);

	const getElementId = (): string | null => ref?.current?.id || null;
	const [dragging, setDragging] = useState(false);
	// const [origin, setPosition] = useState<CoordinatePoint2<number>>(props.bBox?.origin ?? { x: 0, y: 0 });
	const initialPosition = positionOverride ? positionOverride : calcPosition((ref?.current as SVGSVGElement) ?? null) ?? { x: 0, y: 0 };
	const [position, setPosition] = useState<CoordinatePoint2<number>>(initialPosition);
	const [moveTo, setMoveTo] = useState<[CoordinatePoint2<number>, CoordinatePoint2<number>]>([initialPosition, calcFactors(initialPosition, props.bBox)]);
	const [bBox, setBBox] = useState<BBox | null>(props.bBox ?? null);

	const moveToRef = useRef(moveTo);
	moveToRef.current = moveTo;

	useEffect(() => {
		if (positionOverride && !dragging) {
			const [currentPos] = moveToRef.current;
			const xChanged = !Object.is(positionOverride.x, currentPos.x);
			const yChanged = !Object.is(positionOverride.y, currentPos.y);
			if (xChanged || yChanged) {
				const newFactors = props.bBox ? calcFactors(positionOverride, props.bBox) : moveToRef.current[1];
				setPosition(positionOverride);
				setMoveTo([positionOverride, newFactors]);
			}
		}
		if (props.bBox && !dragging && bBox) {
			if (bBox.origin.x !== props.bBox.origin.x || bBox.origin.y !== props.bBox.origin.y ||
				bBox.size.width !== props.bBox.size.width || bBox.size.height !== props.bBox.size.height) {
				setBBox(props.bBox);
			}
		}
	}, [positionOverride, props.bBox]);

	useEffect(() => {
		if (ref?.current) {
			const initialPos = positionOverride ? positionOverride : calcPosition((ref?.current as SVGSVGElement) ?? null) ?? { x: 0, y: 0 };
			const initialFact = calcFactors(initialPos, props.bBox);
			setPosition(initialPos);
			setMoveTo([initialPos, initialFact]);
			setBBox(props.bBox);
		}
	}, [ref?.current]);

	useEffect(() => {
		// console.log('use-draggable-svg:mouse', mouse);
		const id = getElementId();
		if (mouse.isActive && !dragging && mouse.isDragging && (id === null || mouse.selected.includes(id))) {
			setPosition(position);
			setDragging(true);
		} else if (dragging && !mouse.isDragging) {
			setDragging(false);
		}
	}, [mouse.isActive, mouse.isDragging]);

	useEffect(() => {
		if (mouse.isActive && dragging) {
			if (xMove || yMove) {
				// Counter-rotate drag deltas into local coordinate space
				const angleRad = (parentRotation * Math.PI) / 180;
				const cosA = Math.cos(angleRad);
				const sinA = Math.sin(angleRad);
				const localDx = (mouse.drag.dx * cosA) + (mouse.drag.dy * sinA);
				const localDy = (-mouse.drag.dx * sinA) + (mouse.drag.dy * cosA);

				const newPosition = {
					x: xMove ? position.x + localDx : position.x,
					y: yMove ? position.y + localDy : position.y
				};

				const boundary = {
					left: bBox.origin.x - (bBox.size.width / 2) + bBox.padding[0],
					right: bBox.origin.x + (bBox.size.width / 2) - bBox.padding[2],
					top: bBox.origin.y - (bBox.size.height / 2) + bBox.padding[1],
					bottom: bBox.origin.y + (bBox.size.height / 2) - bBox.padding[3]
				};

				if (newPosition.x < boundary.left || newPosition.x > boundary.right) {
					newPosition.x = newPosition.x < boundary.left ? boundary.left : boundary.right;
				}

				if (newPosition.y < boundary.top || newPosition.y > boundary.bottom) {
					newPosition.y = newPosition.y < boundary.top ? boundary.top : boundary.bottom;
				}

				const dragFactors = calcFactors(newPosition, bBox);
				if (onDragging({ position: { old: position, new: newPosition }, factors: { old: factors, new: dragFactors }})) {
					setMoveTo([newPosition, dragFactors]);
				}
			}
		}
	}, [dragging, mouse.drag.distance, xMove, yMove]);

	// timestamp of last movement
	const arrowMoveRef = useRef(0);
	// ms per movement step
	const arrowMoveInterval = 60;

	useEffect(() => {
		const id = getElementId();
		if (!mouse.selected.includes(id) || !bBox) return;

		const now = performance.now();
		if (now - arrowMoveRef.current < arrowMoveInterval) return;

		let screenDx = 0;
		let screenDy = 0;

		if (mouse.keys.includes('arrowup')) screenDy -= 0.0625;
		if (mouse.keys.includes('arrowdown')) screenDy += 0.0625;
		if (mouse.keys.includes('arrowleft')) screenDx -= 0.0625;
		if (mouse.keys.includes('arrowright')) screenDx += 0.0625;

		if (screenDx === 0 && screenDy === 0) return;

		// Counter-rotate arrow key deltas into local coordinate space
		const arrowAngleRad = (parentRotation * Math.PI) / 180;
		const arrowCosA = Math.cos(arrowAngleRad);
		const arrowSinA = Math.sin(arrowAngleRad);
		const deltaX = (screenDx * arrowCosA) + (screenDy * arrowSinA);
		const deltaY = (-screenDx * arrowSinA) + (screenDy * arrowCosA);

		const newPosition = { ...position };
		if (xMove) newPosition.x += deltaX;
		if (yMove) newPosition.y += deltaY;

		const boundary = {
			left: bBox.origin.x - (bBox.size.width / 2) + bBox.padding[0],
			right: bBox.origin.x + (bBox.size.width / 2) - bBox.padding[2],
			top: bBox.origin.y - (bBox.size.height / 2) + bBox.padding[1],
			bottom: bBox.origin.y + (bBox.size.height / 2) - bBox.padding[3]
		};
		newPosition.x = Math.min(Math.max(newPosition.x, boundary.left), boundary.right);
		newPosition.y = Math.min(Math.max(newPosition.y, boundary.top), boundary.bottom);

		const dragFactors = calcFactors(newPosition, bBox);

		if (onDragging({ position: { old: position, new: newPosition }, factors: { old: factors, new: dragFactors }})) {
			setMoveTo([newPosition, dragFactors]);
			setPosition(newPosition);
		}

		arrowMoveRef.current = now;
	}, [mouse.keys, mouse.selected, xMove, yMove]);

	useMemo(() => {
		if (dragging && !mouse.isDragging) {
			if (onDragEnd({ position: { old: position, new: moveTo[0] }, factors: { old: props.factors, new: moveTo[1] }}) === true) {
				setPosition(moveTo[0]);
			} else {
				setMoveTo([position, calcFactors(position, bBox)]);
			}
			// console.log('DRAGGING DONE');
		} else if (!dragging && mouse.isDragging) {
			onDragStart({ position: { old: position, new: moveTo[0] }, factors: { old: props.factors, new: moveTo[1] }, setBBox });
			// console.log('DRAGGING STARTED');
		}
	}, [mouse.isDragging]);

	return [
		{
			position: moveTo[0],
			factors: moveTo[1]
		},
		setBBox
	];
};
